import type { TFunction } from 'i18next';

import { formatDate } from '@/utils/format';

const ENUM_NAMESPACES: Record<string, string> = {
  gender: 'members.gender',
  maritalStatus: 'members.marital',
  memberType: 'members.type',
  status: 'members.status',
  receptionMethod: 'members.reception',
  baptismType: 'members.baptismType',
  marriageType: 'members.marriageType',
  sourceKind: 'members.sourceKind',
};

const DATE_FIELDS = new Set([
  'dateOfBirth',
  'joinedAt',
  'baptismDate',
  'confirmationDate',
  'marriageDate',
  'transferDate',
  'dateOfDeath',
  'burialDate',
  'firstContactOn',
]);

const BOOLEAN_FIELDS = new Set(['isDistantMember', 'hasDisability', 'isCommunicant']);

export const memberHistoryFieldLabel = (field: string, t: TFunction<'people'>): string => {
  const historyKey = `members.detail.historyFields.${field}` as const;
  const translated = t(historyKey);
  if (translated !== historyKey) return translated;

  const formKey = `members.form.${field}` as const;
  const formTranslated = t(formKey);
  if (formTranslated !== formKey) return formTranslated;

  return field;
};

export const formatMemberHistoryValue = (
  field: string,
  value: string | null,
  peerValue: string | null,
  side: 'before' | 'after',
  t: TFunction<'people'>,
  tCommon: TFunction,
): string | null => {
  if (value === null || value === undefined || value === '') return null;

  if (value === '__unavailable__') {
    return t('members.detail.historyValues.unavailable');
  }

  if (field === 'photoKey') {
    if (peerValue) {
      return side === 'before'
        ? t('members.detail.historyValues.previousPhoto')
        : t('members.detail.historyValues.newPhoto');
    }
    return t('members.detail.historyValues.photoSet');
  }

  if (BOOLEAN_FIELDS.has(field)) {
    if (value === 'true') return tCommon('boolean.yes');
    if (value === 'false') return tCommon('boolean.no');
  }

  const enumNs = ENUM_NAMESPACES[field];
  if (enumNs) {
    const key = `${enumNs}.${value}`;
    const translated = t(key as 'members.status.active');
    if (translated !== key) return translated;
  }

  if (DATE_FIELDS.has(field)) {
    return formatDate(value);
  }

  return value;
};
